import { Folders } from 'owa-mail-folder-tree-container-view/lib/components/PrimaryMailFolderTreeContainer.locstring.json';
import loc from 'owa-localize';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import MailFolderListWrapper from './MailFolderListWrapper';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getMailRootFolderChildIds } from 'owa-folders';
import { getFoldersToShowFirst } from 'owa-folders-hierarchy-bootstrap';
import { lazyToggleFolderTreeExpansion } from 'owa-mail-folder-store';
import { lazyDeleteFolderInFolderForest } from 'owa-mail-folder-forest-actions';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { ActionSource } from 'owa-mail-store';
import { isFolderUnderDeletedItemsFolder } from 'owa-mail-store';
import { getPrimaryFolderTreeRootFolder, getPrimaryMailFolders } from 'owa-folders';
import { MailFolder } from 'owa-graph-schema';
import {
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_FOLDERS_TREE_TYPE,
} from 'owa-folders-constants';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { NewsNode } from 'owa-mail-news';
import { isNewsEnabled } from 'owa-favorites';
import { isFeatureEnabled } from 'owa-feature-flags';

export interface PrimaryMailFolderTreeContainerProps {
    showRootNode: boolean;
    className: string;
    setSize?: number;
    positionInSet?: number;
}

/**
 * Primary mailbox container
 */
export default observer(function PrimaryMailFolderTreeContainer(
    props: PrimaryMailFolderTreeContainerProps
) {
    // Primary mailbox root click handler
    const onRootNodeChevronClicked = () => {
        lazyToggleFolderTreeExpansion.importAndExecute();
    };

    const onDeleteFolder = (folder: MailFolder, actionSource: ActionSource) => {
        const primaryDeletedItemsFolderId = folderNameToId(PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID);
        const isFolderUnderDeletedItemFolder = isFolderUnderDeletedItemsFolder(
            folder,
            PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID
        );
        lazyDeleteFolderInFolderForest.importAndExecute(
            folder.FolderId.Id,
            actionSource,
            primaryDeletedItemsFolderId,
            isFolderUnderDeletedItemFolder
        );
    };
    const renderCustomNode = (lastRenderedId: string, nestDepth: number): JSX.Element => {
        const foldersToShowFirst = getFoldersToShowFirst();

        // Add news after the default folders
        const shouldAddNews =
            isNewsEnabled() &&
            nestDepth === 1 &&
            foldersToShowFirst.indexOf(folderIdToName(lastRenderedId)) ===
                foldersToShowFirst.length - 1;

        if (!shouldAddNews) {
            return null;
        } else {
            return <NewsNode treeType={'primaryFolderTree'} key={'news'} />;
        }
    };

    const onLoadMoreClicked = () => {
        getPrimaryMailFolders(true /* isLoadingMore */);
    };

    const userConfiguration = getUserConfiguration();
    const sessionSettings = userConfiguration?.SessionSettings;
    const isExplicitLogon = sessionSettings?.IsExplicitLogon;
    const rootDisplayName = isFeatureEnabled('mon-tri-leftNavChanges')
        ? sessionSettings?.UserEmailAddress || loc(Folders)
        : loc(Folders);

    return (
        <MailFolderListWrapper
            className={props.className}
            key="primarymailboxtree"
            showRootNode={props.showRootNode}
            rootFolder={getPrimaryFolderTreeRootFolder()}
            isRootExpanded={!userConfiguration.UserOptions.IsMailRootFolderTreeCollapsed}
            rootDisplayName={rootDisplayName}
            childFolderIds={getMailRootFolderChildIds('primary')}
            onRootNodeChevronClickedCallback={onRootNodeChevronClicked}
            treeType={PRIMARY_FOLDERS_TREE_TYPE}
            isFavoritesSupported={!isExplicitLogon} // Favorites not supported for explicit logon scenarios
            onDeleteFolderCallback={onDeleteFolder}
            rootNodeId={PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID}
            shouldBeDroppable={true}
            onLoadMoreClickedCallback={onLoadMoreClicked}
            renderCustomNode={renderCustomNode}
            setSize={props.setSize}
            positionInSet={props.positionInSet}
        />
    );
});
